import _ from 'lodash';
import { COUNTRY_MAPPING } from '../../../../../../i18n/i18n.language-config';
import type { SubmissionDetailsDto } from '../../../../../../api/submissions/submissions.api.types';
import { useCountryApiParam } from '../../../../../../utils/hooks/useCountry';
import type { Nullable } from '../../../../../../types/common/utility';
import type { ProductLineOption } from '../../../../../../components/inputs/selects/ProductLineSelect';

type FormValues = {
  [key: string]: any;
};

export const toUpdateSubmissionDto = (formValues: FormValues) => {
  return {
    firmographics: {
      ..._.omit(formValues, [
        'address1',
        'address2',
        'address3',
        'city',
        'state',
        'zipCode',
        'country',
        'domains',
        'revenue',
        'otherRevenue',
        'totalRevenue',
      ]),
      ...(formValues.address1 ||
      formValues.address2 ||
      formValues.address3 ||
      formValues.city ||
      formValues.state ||
      formValues.zipCode
        ? { physicalAddress: formValuesToAddressDto(formValues) }
        : {}),
      ...(formValues.domains
        ? { otherDomains: formValuesToOtherDomainsDto(formValues) }
        : {}),
      ...(_.isNumber(formValues.revenue)
        ? { domesticRevenue: formValues.revenue }
        : {}),
      ...(_.isNumber(formValues.otherRevenue)
        ? { foreignRevenue: formValues.otherRevenue }
        : {}),
    },
  };
};

const formValuesToAddressDto = (formValues: FormValues) => {
  return {
    address1: formValues.address1,
    address2: formValues.address2,
    address3: formValues.address3,
    city: formValues.city,
    state: formValues.state,
    zipCode: formValues.zipCode,
    country: formValues.country,
  };
};

const formValuesToOtherDomainsDto = (formValues: FormValues) => {
  return formValues.domains.split(',');
};

export const getCountryName = (submission: SubmissionDetailsDto) => {
  return COUNTRY_MAPPING[submission.firmographics?.physicalAddress?.country];
};

/**
 * @deprecated Misnamed — it returns the `country` value to send to the API, not a country name.
 * Prefer `useCountryApiParam` from utils/hooks/useCountry directly; this alias remains only because
 * 14 call sites import it from here.
 */
export const useCountryNameByPlatform = useCountryApiParam;

export const formValuesToPolicyContactDto = (formValues: FormValues) => {
  return {
    firstName: formValues.contactFirstName,
    lastName: formValues.contactLastName,
    emailAddress: formValues.contactEmail,
    phoneNumber: formValues.contactPhone,
  };
};

export const MANAGEMENT_LIABILITY_KEY = 'ML';

export const isMLChildProductLine = (
  lines: Nullable<SubmissionDetailsDto['productLines']>,
  productLineOptions: ProductLineOption[]
): boolean => {
  if (!lines || !lines.length || !productLineOptions.length) {
    return false;
  }

  return (
    productLineOptions
      .find((line) => line.value === MANAGEMENT_LIABILITY_KEY)
      ?.children?.some((c) => c.value === lines[0].key) ?? false
  );
};

export const formValuesToSecurityContactDto = (formValues: FormValues) => {
  return {
    firstName: formValues.securityContactFirstName,
    lastName: formValues.securityContactLastName,
    email: formValues.securityContactEmailAddress,
    phoneNumber: formValues.securityContactPhoneNumber,
    title: formValues.securityContactTitle,
    company: formValues.securityContactCompanyName,
  };
};
